import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { DeviceTokenService } from './device-token.service';

/** Shape returned by GET /devices/token. */
interface AppOnlyTokenResponse {
  readonly access_token: string;
  readonly expires_in: number;
}

const STORAGE_KEY = 'gtlc-mobile-staff-app-token';

/**
 * Bootstraps the tablet's outer transport credential — the app-only Azure access
 * token returned by GET /devices/token. This token (Authorization: Bearer) makes
 * the global JWTAzure.validateJWT() gate accept tablet requests; the inner staff
 * identity is carried separately by the badge-session token (X-Staff-Session-Token).
 *
 * The token is cached locally and refreshed proactively before it expires, so
 * tablet API calls always present a valid bearer. The device token (X-Device-Token)
 * is the gating credential for fetching/refreshing it.
 *
 * Because the app-only token carries no gtlc.acl.* claim, it can never elevate the
 * staff tier — server-side Position-ACL resolution remains authoritative.
 */
@Injectable({ providedIn: 'root' })
export class DeviceBootstrapService {
  private readonly http = inject(HttpClient);
  private readonly deviceToken = inject(DeviceTokenService);

  /** In-memory + persisted cache of the app-only token (or null). */
  #accessToken: string | null = this.readFromStorage();
  #expiresAt = 0;

  /** Returns a valid app-only token, fetching or refreshing if needed. */
  async getAccessToken(): Promise<string | null> {
    const deviceToken = this.deviceToken.token();
    if (!deviceToken) return null;

    const now = Math.floor(Date.now() / 1000);
    // Refresh proactively with a 60s margin before expiry.
    if (this.#accessToken && now < this.#expiresAt - 60) {
      return this.#accessToken;
    }

    try {
      const res = await firstValueFrom(
        this.http.get<AppOnlyTokenResponse>(`${environment.apiUrl}/devices/token`, {
          headers: { 'X-Device-Token': deviceToken },
        }),
      );
      this.#accessToken = res.access_token;
      this.#expiresAt = now + res.expires_in;
      this.persist(res.access_token);
      return this.#accessToken;
    } catch {
      return null;
    }
  }

  /** Attach the app-only bearer to an existing headers object (or return {} if unavailable). */
  async authHeader(): Promise<Record<string, string>> {
    const token = await this.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /** Clear the cached app-only token (e.g. on device revocation / sign-out). */
  clear(): void {
    this.#accessToken = null;
    this.#expiresAt = 0;
    localStorage.removeItem(STORAGE_KEY);
  }

  private persist(token: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, token);
    } catch {
      // Storage full/blocked — the in-memory value still works for this session.
    }
  }

  private readFromStorage(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
